import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellHead, CellHeadProps } from './CellHead';
export { CellHead, cellHead } from './CellHead';
export type { CellHeadProps } from './CellHead';
import {
  IconButton,
  Menu,
  Stack,
  MenuItem,
  Typography,
  Box,
  Tooltip,
  Select,
  Skeleton,
} from '@mui/material';
import { FC, useCallback, useRef, useState } from 'react';
import { colors } from '../colors';
import {
  AnyObject,
  F0,
  TypedOmit,
  Option,
  F1,
} from '@client/new/helpers/types';
import { Link } from 'react-router';
import classnames from 'classnames';
import { Typography as NewTypography } from '@design-system/components/typography/typography';
import { clsx } from 'clsx';
import { sprinkles } from '@design-system/sprinkles.css';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  ButtonProps as DSButtonProps,
} from '@design-system/components/button/button';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';

type TooltipType = {
  tooltip?: string;
};

type ClassNamesCellIcon = {
  classNameWrapper?: string;
  classNameIcon?: string;
  classNameLabel?: string;
};
export type CellIconTextProps = {
  type: 'IconText';
  value: {
    label?: string | number;
    icon?: IconProp;
    classNames?: ClassNamesCellIcon;
  } & TooltipType;
};

const withTooltip =
  <T extends AnyObject | ({ value?: { tooltip?: TooltipType } } & AnyObject)>(
    Component: FC<T>,
  ) =>
  (props: T) => {
    if (!props?.value?.tooltip) return <Component {...props} />;

    return (
      <Tooltip title={props.value.tooltip} arrow>
        <Box>
          <Component {...props} />
        </Box>
      </Tooltip>
    );
  };

export const cellIconText = (
  text: CellIconTextProps['value']['label'],
  icon?: CellIconTextProps['value']['icon'],
  classNames?: CellIconTextProps['value']['classNames'],
): CellIconTextProps => ({
  type: 'IconText',
  value: { label: text, icon, classNames },
});

const CellIconText = (p: CellIconTextProps['value']) => (
  <div className={p.classNames?.classNameWrapper}>
    {p.icon ? (
      <FontAwesomeIcon
        icon={p.icon}
        style={{ paddingRight: '0.5em' }}
        className={p.classNames?.classNameIcon}
      />
    ) : null}
    {p.label !== undefined ? (
      <Typography
        variant="body2"
        component="span"
        className={p.classNames?.classNameLabel}
      >
        {p.label}
      </Typography>
    ) : null}
  </div>
);

export type CellSelectItem<T = string> = Option<T> & { disabled?: boolean };

export type CellSelectProps<T = string> = {
  type: 'Select';
  value: {
    value?: T;
    onChange: F1<T>;
    toValue: F1<string, T>;
    fromValue: F1<T, string>;
    items: Array<CellSelectItem<T>>;
  };
};

const CellSelect = <T = string,>(p: CellSelectProps<T>['value']) => (
  <div>
    <Select<string>
      value={p.value ? p.fromValue(p.value) : undefined}
      onChange={(e) => p.onChange(p.toValue(e.target.value))}
    >
      {p.items.map((item, i) => (
        <MenuItem
          key={i}
          value={p.fromValue(item.value)}
          disabled={item.disabled}
        >
          {item.label}
        </MenuItem>
      ))}
    </Select>
  </div>
);

export type CellFetchingProps = {
  type: 'Busy';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
};

const CellFetching = () => (
  <div
    style={{
      height: 30,
      borderRadius: 4,
      animation: 'gradient 1.5s ease infinite',
      backgroundSize: '400%',
      // must be backgroundImage instead of background to work with backgroundSize
      backgroundImage:
        'linear-gradient(270deg, rgba(30, 33, 52, 0.06) 0%, rgba(30, 33, 52, 0.03) 100%)',
    }}
  />
);

type CellImageProps = { type: 'Image'; value: { url: string } };
const CellImage = (p: CellImageProps['value']) => (
  <img src={p.url} style={{ maxHeight: '80px' }} />
);

export type ButtonProps = {
  type: 'Button';
  value: {
    title?: string;
    icon?: IconProp;
    type?: 'primary' | 'neutral';
    onClick: () => void;
    disabled?: boolean;
  } & TooltipType &
    DSButtonProps;
};

export type DownloadLinkProps = {
  type: 'DownloadLink';
  value: LinkButtonProps['value'] & { suggestedFilename: string };
};

export type LinkButtonProps = {
  type: 'LinkButton';
  value: { to: string } & TypedOmit<ButtonProps['value'], 'onClick'> &
    Partial<Pick<ButtonProps['value'], 'onClick'>>;
};

export type ExternalLinkButtonProps = {
  type: 'ExternalLinkButton';
  value: { href: string } & TypedOmit<ButtonProps['value'], 'onClick'> &
    Partial<Pick<ButtonProps['value'], 'onClick'>>;
};

export type LinkProps = {
  type: 'Link';
  value: { to: string; external?: boolean } & CellIconTextProps['value'];
};

export type CellCTAProps =
  | LinkButtonProps
  | ButtonProps
  | ExternalLinkButtonProps
  | LinkProps
  | DownloadLinkProps;

export type CellCTAItemsProps = {
  type: 'CTA items';
  value: {
    items: Array<CellCTAProps>;
  };
};

export const CTAButton = (
  p: Omit<ButtonProps['value'], 'onClick'> &
    Partial<Pick<ButtonProps['value'], 'onClick'>>,
) =>
  p.icon && !p.title ? (
    <IconButton
      disabled={p.disabled}
      onClick={p.onClick}
      className={`${p.disabled ? 'text-gray-5' : 'text-black'}`}
      sx={{ color: 'red' }}
    >
      {p.title && <p>{p.title}</p>}
      {p.icon && (
        <FontAwesomeIcon
          icon={p.icon}
          size="xs"
          color={p.type === 'primary' ? '#000' : '#999'}
        />
      )}
    </IconButton>
  ) : (
    <Button
      disabled={p.disabled}
      onClick={p.onClick}
      variant={p?.variant || 'filled'}
      color={p?.color || 'primary'}
      className={p?.className}
      rightIcon={p.icon}
    >
      {p.title}
    </Button>
  );

const CellLink = (p: LinkProps) =>
  p.value.external ? (
    <a href={p.value.to} target="_blank" rel="noopener noreferrer">
      <CellIconText {...p.value} />
    </a>
  ) : (
    <Link to={p.value.to}>
      <CellIconText {...p.value} />
    </Link>
  );

export const cta = (
  icon: ButtonProps['value']['icon'],
  param: F0 | string,
  disabled?: boolean,
  tooltip?: string,
): ButtonProps | LinkButtonProps =>
  typeof param === 'string'
    ? {
        type: 'LinkButton',
        value: {
          icon,
          to: param,
          tooltip,
          disabled,
        },
      }
    : {
        type: 'Button',
        value: {
          icon,
          onClick: param,
          tooltip,
          disabled,
        },
      };

const CellCTARaw: FC<CellCTAProps> = (p) => {
  switch (p.type) {
    case 'Button':
      return <CTAButton {...p.value} />;
    case 'LinkButton':
      return (
        <Link
          to={p.value.to}
          className={classnames({ 'pointer-events-none': p.value.disabled })}
        >
          <CTAButton {...p.value} />
        </Link>
      );
    case 'Link':
      return <CellLink {...p} />;
    case 'DownloadLink':
      return (
        <a
          href={p.value.to}
          className={classnames({ 'pointer-events-none': p.value.disabled })}
          download={p.value.suggestedFilename}
          rel="noreferrer"
        >
          <CTAButton {...p.value} />
        </a>
      );
    case 'ExternalLinkButton':
      return (
        <Link
          href={p.value.href}
          to={p.value.href}
          className={classnames({ 'pointer-events-none': p.value.disabled })}
        >
          <CTAButton {...p.value} />
        </Link>
      );
  }
};

// TODO (h): unify with CTA on Checklist Item
const CellCTA = withTooltip(CellCTARaw);

export const cellCTAItems = (
  value: CellCTAItemsProps['value'],
): CellCTAItemsProps => ({ type: 'CTA items', value });

export const CellCTAItems = (p: CellCTAItemsProps['value']) => (
  <Box className="flex gap-2">
    {p.items.map((item, index) => (
      <CellCTA key={index} {...item} />
    ))}
  </Box>
);

export type IMenuItem = {
  text: string;
  onClick?: () => void;
  icon?: IconProp;
  to?: string;
  href?: string;
  disabled?: boolean;
  className?: string;
  secondaryText?: string;
  tooltip?: string;
  color?: 'error';
};
export type MenuItems = Array<IMenuItem>;

export type CellMenuProps = {
  type: 'Menu';
  value: {
    items: MenuItems;
  };
};

export type RowProps =
  | { type: 'DividerRow'; value: AnyObject }
  | { type: 'TitleRow'; value: string };

export type CellProps<T = string> =
  | RowProps
  | { type: 'Items'; value: Array<CellProps<T>> }
  | CellIconTextProps
  | CellImageProps
  | CellHeadProps
  | CellMenuProps
  | CellCTAItemsProps
  | CellCTAProps
  | CellSelectProps<T>
  | CellFetchingProps
  | { type: 'Custom'; value: JSX.Element };

export function usePopover<T>() {
  const anchorRef = useRef<T | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setOpen((prevState) => !prevState);
  }, []);

  return {
    anchorRef,
    handleClose,
    handleOpen,
    handleToggle,
    open,
  };
}

export const cellMenu = (value: CellMenuProps['value']): CellMenuProps => ({
  type: 'Menu',
  value,
});

export const CellMenu = (p: CellMenuProps['value']) => {
  const popover = usePopover<HTMLButtonElement>();

  return (
    <>
      <IconButton
        style={{ zIndex: 10 }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          popover.handleOpen();
        }}
        ref={popover.anchorRef}
      >
        <FontAwesomeIcon
          icon={faEllipsis}
          className="text-black text-[20px] font-normal"
        />
      </IconButton>
      <Menu
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        disableScrollLock
        open={popover.open}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClose={(e: any) => {
          popover.handleClose();
          e?.stopPropagation();
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root > .MuiMenu-list': {
            padding: 0,
          },
        }}
      >
        {p.items.map((item, index) => (
          <CellMenuItem key={'item' + index} item={item} popover={popover} />
        ))}
      </Menu>
    </>
  );
};

export const CellMenuItem = ({
  item,
  popover,
}: {
  item: IMenuItem;
  popover: ReturnType<typeof usePopover>;
}) => {
  if (item.to) {
    return (
      <Link
        to={item.to}
        className={clsx(
          'text-black',
          item?.disabled && 'pointer-events-none cursor-not-allowed',
        )}
        onClick={(ev) => {
          ev.stopPropagation();
          trackSessionEvent('LinkClick', {
            content: JSON.stringify(item),
            to: item.to || '',
          });
          item?.disabled && ev.preventDefault();
        }}
      >
        <div
          className={clsx(
            'p-4 hover:bg-[#1018280a]',
            item?.disabled && 'cursor-not-allowed',
          )}
        >
          <Stack alignItems="left" direction="row" spacing={1} gap={1}>
            {item.icon ? (
              <NewTypography variant="body" size="large">
                <FontAwesomeIcon icon={item.icon} />
              </NewTypography>
            ) : null}
            <div className="flex flex-col" style={{ minWidth: 0 }}>
              <Tooltip title={item.tooltip}>
                <NewTypography
                  variant="body"
                  size="large"
                  className={clsx(
                    'w-full h-full',
                    item.className,
                    item.disabled && sprinkles({ color: 'N-60' }),
                    item.color === 'error' && sprinkles({ color: 'ERROR-100' }),
                  )}
                >
                  {item.text}
                </NewTypography>
              </Tooltip>
              {!item?.secondaryText ? null : (
                <NewTypography
                  variant="body"
                  className={clsx(
                    item.className,
                    item.disabled && sprinkles({ color: 'N-60' }),
                  )}
                  style={{
                    maxWidth: '27ch',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                  }}
                >
                  {item?.secondaryText}
                </NewTypography>
              )}
            </div>
          </Stack>
        </div>
      </Link>
    );
  }

  return (
    <MenuItem
      onClick={(e) => {
        e.stopPropagation();
        trackSessionEvent('ButtonClick', {
          content: JSON.stringify(item),
        });
        popover.handleClose();
        item.onClick && !item?.disabled && item.onClick();
      }}
      className={clsx('p-4', item?.disabled && 'cursor-not-allowed')}
    >
      <Stack alignItems="left" direction="row" spacing={1} gap={1}>
        {item.icon ? (
          <NewTypography variant="body" size="large">
            <FontAwesomeIcon icon={item.icon} />
          </NewTypography>
        ) : null}
        <div className="flex flex-col" style={{ minWidth: 0 }}>
          {item?.href ? (
            <a
              className="text-black"
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              <Tooltip title={item.tooltip}>
                <NewTypography
                  variant="body"
                  size="large"
                  className={clsx(
                    item.className,
                    item.disabled && sprinkles({ color: 'N-60' }),
                    item.color === 'error' && sprinkles({ color: 'ERROR-100' }),
                  )}
                >
                  {item.text}
                </NewTypography>
              </Tooltip>
            </a>
          ) : (
            <Tooltip title={item.tooltip}>
              <NewTypography
                variant="body"
                size="large"
                className={clsx(
                  item.className,
                  item.disabled && sprinkles({ color: 'N-60' }),
                  item.color === 'error' && sprinkles({ color: 'ERROR-100' }),
                )}
              >
                {item.text}
              </NewTypography>
            </Tooltip>
          )}
          {!item?.secondaryText ? null : (
            <NewTypography
              variant="body"
              className={clsx(
                item.className,
                item.disabled && sprinkles({ color: 'N-60' }),
              )}
              style={{
                maxWidth: '27ch',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
              }}
            >
              {item?.secondaryText}
            </NewTypography>
          )}
        </div>
      </Stack>
    </MenuItem>
  );
};

export const Cell = withTooltip(
  (p: CellProps & { index?: number }): React.ReactElement => {
    switch (p.type) {
      case 'Items':
        return (
          <div className="flex gap-2 items-center">
            {p.value.map((c, i) => (
              <Cell {...c} key={i} />
            ))}
          </div>
        );
      case 'DividerRow':
        return (
          <td>
            <Skeleton
              animation={false}
              height={1}
              sx={{ background: colors.secondary200 }}
            />
          </td>
        );
      case 'TitleRow':
        return (
          <td>
            <Box
              sx={{
                padding: '0 10px',
                background: colors.secondary200,
                borderRadius: '5px',
              }}
            >
              <Typography variant="overline">{p.value}</Typography>
            </Box>
          </td>
        );
      case 'Head':
        return <CellHead order={(p.index ?? 0) + 1} {...p.value} />;
      case 'IconText':
        return <CellIconText {...p.value} />;
      case 'Image':
        return <CellImage {...p.value} />;
      case 'Busy':
        return <CellFetching />;
      case 'Menu':
        return <CellMenu {...p.value} />;
      case 'Select':
        return <CellSelect {...p.value} />;
      case 'CTA items':
        return <CellCTAItems {...p.value} />;
      case 'Button':
      case 'LinkButton':
      case 'ExternalLinkButton':
      case 'Link':
        return <CellCTA {...p} />;
      case 'Custom':
        return <>{p.value}</>;
      default:
        return <></>;
    }
  },
);
